import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const { Howler } = require('howler');
import { disableIdleAudioSuspend, installAudioResumeHandlers } from 'utils/audioResume';

type GlobalContextType = {
  isLoading: any;
  openInfoMsg: any;
  openAskBuyin: boolean;
  /**
   * WHICH table the open buy-in belongs to — bound at click time by whichever
   * surface the player acted on (JoinButton, an open seat, a ready waitlist
   * offer). openAskBuyin alone is a bare boolean shared app-wide in single
   * view, so with several keep-alive panels mounted EVERY panel's BuyInModal
   * opened at once, stacked, and whichever happened to sit on top answered the
   * click with ITS OWN table id, game type, and seat math. That is the single
   * root behind the whole join whack-a-mole (owner RCA, 8/25): "Table is
   * already full!" on an empty felt, "You are already seated!", "Invalid
   * Seat #N!", and the silent do-nothing were all the wrong panel's modal
   * winning the stack. BuyInModal renders only in the panel this names, and
   * the join is emitted with exactly this identity.
   */
  askBuyinTable: { gameID: string; game: string } | null;
  setAskBuyinTable: (t: { gameID: string; game: string } | null) => void;
  buyinAmount: any;
  isBuyinValueSet: any;
  userSession: any;
  openAccessTokenModal: any;
  manuallyAssignedSeatId: number;
  openWaitForBuyIn: boolean;
  openAddFunds: boolean;
  openErrorModal: boolean;
  isJoining: boolean;
  gameType: string;
  closeBuyInModal: boolean;
  isLocated: boolean;
  /**
   * Which geolocation implementation THIS login committed to.
   *
   * `null` until LaunchDarkly has decided; `true` legacy, `false` vendor. It
   * lives here rather than in Lobby because two components must agree on it:
   * Lobby gates the socket connect, and App decides whether a refusal should
   * tear the socket down. Reading the live flag in either place lets them
   * diverge from the check that actually ran -- the flag can change after the
   * path is pinned, and then the gate and the verdict describe different
   * implementations.
   */
  committedGeoLegacy: boolean | null;
  /**
   * A login-time location check is in flight.
   *
   * Lifted out of Lobby's local state because two things outside Lobby need it:
   * the connection banner, which must not claim a network fault while we are
   * merely waiting on a verdict, and the socket hold, which defers the
   * guest-to-member handshake until there is a verdict to admit on.
   */
  geoCheckPending: boolean;
  isLocationInvalid: boolean;
  invalidLocationMsg: string;
  deviceId: string;
  duplicateAccessMsg: string;
  isDuplicateAccess: boolean;
  // Engine ships the gameID + game type of the table that triggered the
  // duplicate-access kick. Grid view uses these to surgically remove only
  // that one table from the watcher list instead of returning to the lobby
  // and tearing down every other open table.
  duplicateAccessGameID: string;
  duplicateAccessGame: string;
  isTriggerBuyIn: boolean;
  openAskPassword: boolean;
  askPasswordMsg: string;
  setIsLoading: (flag: boolean) => void;
  setOpenAskBuyin: (flag: boolean) => void;
  setBuyinAmount: (amount: number) => void;
  setOpenAddFunds: (flag: boolean) => void;
  setOpenErrorModal: (flag: boolean) => void;
  setIsBuyinValueSet: (flag: boolean) => void;
  setInfoMsg: (tables: any) => any;
  setUserSession: (userSession: any) => any;
  setOpenAccessTokenModal: (flag: boolean) => void;
  setManuallyAssignedSeatId: (seatId: number) => void;
  setOpenWaitForBuyIn: (flag: boolean) => void;
  setIsJoining: (value: boolean) => void;
  setGameType: (value: string) => void;
  setCloseBuyInModal: (flag: boolean) => void;
  setIsLocated: (flag: boolean) => void;
  setCommittedGeoLegacy: (value: boolean | null) => void;
  setGeoCheckPending: (value: boolean) => void;
  setIsLocationInvalid: (flag: boolean) => void;
  setInvalidLocationMsg: (msg: string) => void;
  setDeviceId: (deviceID: string) => void;
  setDuplicateAccessMsg: (msg: string) => void;
  setIsDuplicateAccess: (flag: boolean) => void;
  setDuplicateAccessGameID: (gameID: string) => void;
  setDuplicateAccessGame: (game: string) => void;
  setIsTriggerBuyIn: (value: boolean) => void;
  setOpenAskPassword: (value: boolean) => void;
  setAskPasswordMsg: (value: string) => void;
  isMuted: boolean;
  setIsMuted: (flag: boolean) => void;
};

const globalContextDefault: GlobalContextType = {
  isLoading: false,
  openInfoMsg: false,
  openAskBuyin: false,
  askBuyinTable: null,
  setAskBuyinTable: () => {
    // Outside GlobalProvider the binding is dropped and the buy-in modal falls
    // back to the legacy shared-flag behaviour (SQE scan, Marlon 8/25 MED) —
    // say so in dev instead of failing silently.
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[GlobalContext] setAskBuyinTable called outside GlobalProvider — buy-in intent not bound'
      );
    }
  },
  buyinAmount: false,
  isBuyinValueSet: false,
  userSession: {
    user: 0
  },
  openAccessTokenModal: false,
  manuallyAssignedSeatId: 0,
  openWaitForBuyIn: false,
  openAddFunds: false,
  openErrorModal: false,
  isJoining: false,
  gameType: '',
  closeBuyInModal: false,
  isLocated: false,
  committedGeoLegacy: null,
  geoCheckPending: false,
  isLocationInvalid: false,
  invalidLocationMsg: '',
  deviceId: '',
  duplicateAccessMsg: '',
  isDuplicateAccess: false,
  duplicateAccessGameID: '',
  duplicateAccessGame: '',
  isTriggerBuyIn: false,
  openAskPassword: false,
  askPasswordMsg: '',
  setIsLoading: (_) => {},
  setInfoMsg: (_) => {},
  setOpenAskBuyin: (_) => {},
  setBuyinAmount: (_) => {},
  setIsBuyinValueSet: (_) => {},
  setUserSession: (_) => {},
  setOpenAccessTokenModal: (_) => {},
  setManuallyAssignedSeatId: (_) => {},
  setOpenWaitForBuyIn: (_) => {},
  setIsJoining: (_) => {},
  setGameType: (_) => {},
  setOpenAddFunds: (_) => {},
  setOpenErrorModal: (_) => {},
  setCloseBuyInModal: (_) => {},
  setIsLocated: (_) => {},
  setCommittedGeoLegacy: (_) => {},
  setGeoCheckPending: (_) => {},
  setIsLocationInvalid: (_) => {},
  setInvalidLocationMsg: (_) => {},
  setDeviceId: (_) => {},
  setDuplicateAccessMsg: (_) => {},
  setIsDuplicateAccess: (_) => {},
  setDuplicateAccessGameID: (_) => {},
  setDuplicateAccessGame: (_) => {},
  setIsTriggerBuyIn: (_) => {},
  setOpenAskPassword: (_) => {},
  setAskPasswordMsg: (_) => {},
  isMuted: false,
  setIsMuted: (_) => {}
};

const GlobalContext = createContext<GlobalContextType>(globalContextDefault);

export const GlobalProvider = ({ children }: PropsWithChildren) => {
  const [isLoading, setIsLoading] = useState(true);
  const [openInfoMsg, setInfoMsg] = useState(false);
  const [openAskBuyin, setOpenAskBuyinState] = useState(false);
  const [askBuyinTable, setAskBuyinTable] = useState<{ gameID: string; game: string } | null>(null);
  // Closing the modal clears the bound table with it — a stale binding would
  // gate the NEXT open into the wrong panel.
  const setOpenAskBuyin = useCallback((open: boolean) => {
    setOpenAskBuyinState(open);
    if (!open) setAskBuyinTable(null);
  }, []);
  const [manuallyAssignedSeatId, setManuallyAssignedSeatId] = useState(0);
  const [buyinAmount, setBuyinAmount] = useState(0);
  const [isBuyinValueSet, setIsBuyinValueSet] = useState(false);
  const [userSession, setUserSession] = useState(
    JSON.parse(localStorage.getItem('userSession') || '{}')
  );
  const [openAccessTokenModal, setOpenAccessTokenModal] = useState(false);
  const [openWaitForBuyIn, setOpenWaitForBuyIn] = useState(false);
  const [openAddFunds, setOpenAddFunds] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [gameType, setGameType] = useState('texas');
  const [closeBuyInModal, setCloseBuyInModal] = useState(false);
  const [isLocated, setIsLocated] = useState(false);
  const [committedGeoLegacy, setCommittedGeoLegacy] = useState<boolean | null>(null);
  const [geoCheckPending, setGeoCheckPending] = useState(false);
  const [isLocationInvalid, setIsLocationInvalid] = useState(false);
  const [invalidLocationMsg, setInvalidLocationMsg] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [duplicateAccessMsg, setDuplicateAccessMsg] = useState('');
  const [isDuplicateAccess, setIsDuplicateAccess] = useState(false);
  const [duplicateAccessGameID, setDuplicateAccessGameID] = useState('');
  const [duplicateAccessGame, setDuplicateAccessGame] = useState('');
  const [isTriggerBuyIn, setIsTriggerBuyIn] = useState(false);
  const [openAskPassword, setOpenAskPassword] = useState(false);
  const [askPasswordMsg, setAskPasswordMsg] = useState('');
  const [isMuted, setIsMutedState] = useState(
    () => localStorage.getItem('hijack_muted') === 'true'
  );

  const setIsMuted = useCallback((flag: boolean) => {
    setIsMutedState(flag);
    Howler.mute(flag);
  }, []);

  useEffect(() => {
    //Update Session Data here
    localStorage.setItem('userSession', JSON.stringify(userSession));
  }, [userSession]);

  // The audio.mp3 sprite sheet is GONE (owner review, 2026-08-27). Its two
  // live cues became individual files (placing_bet.wav, collect_chips.wav),
  // three maybes were extracted as small_bet / table_card / shuffle (small_bet
  // has since been implemented as the single-chip wager cue), and the
  // rest were deleted. That also ends this file's 872KB per-session download
  // and retires playSound - every cue now goes through hooks/useSound, where
  // the table gating, dedupe and the loudness ladder live.

  // Persist mute preference + apply on load
  useEffect(() => {
    localStorage.setItem('hijack_muted', String(isMuted));
    Howler.mute(isMuted);
  }, [isMuted]);

  // Bring the shared AudioContext back when the app returns to the foreground.
  // Without this, backgrounding the app on a device suspends the context and
  // Howler never resumes it, so every later sound is silently dropped until the
  // app is restarted. See utils/audioResume for the mechanism.
  useEffect(() => {
    // Stop Howler suspending the context after 30s of table silence — otherwise
    // the next cue (most visibly the showdown card flip) is parked until an async
    // ctx.resume() round-trips, and arrives late. See utils/audioResume.
    disableIdleAudioSuspend(Howler);
    return installAudioResumeHandlers(Howler);
  }, []);

  // PERF: memoized so consumers only re-render when one of these values
  // actually changes, not on every provider re-render. See
  // docs/investigations/20260819-game-client-overheating.md.
  const value = useMemo(
    () => ({
      isLoading,
      setIsLoading,
      openInfoMsg,
      setInfoMsg,
      openAskBuyin,
      setOpenAskBuyin,
      askBuyinTable,
      setAskBuyinTable,
      buyinAmount,
      setBuyinAmount,
      isBuyinValueSet,
      setIsBuyinValueSet,
      setUserSession,
      userSession,
      openAccessTokenModal,
      setOpenAccessTokenModal,
      manuallyAssignedSeatId,
      setManuallyAssignedSeatId,
      openWaitForBuyIn,
      setOpenWaitForBuyIn,
      openAddFunds,
      setOpenAddFunds,
      openErrorModal,
      setOpenErrorModal,
      isJoining,
      setIsJoining,
      gameType,
      setGameType,
      closeBuyInModal,
      setCloseBuyInModal,
      isLocated,
      committedGeoLegacy,
      setCommittedGeoLegacy,
      geoCheckPending,
      setGeoCheckPending,
      setIsLocated,
      isLocationInvalid,
      setIsLocationInvalid,
      invalidLocationMsg,
      setInvalidLocationMsg,
      deviceId,
      setDeviceId,
      duplicateAccessMsg,
      setDuplicateAccessMsg,
      isDuplicateAccess,
      setIsDuplicateAccess,
      duplicateAccessGameID,
      setDuplicateAccessGameID,
      duplicateAccessGame,
      setDuplicateAccessGame,
      isTriggerBuyIn,
      setIsTriggerBuyIn,
      openAskPassword,
      setOpenAskPassword,
      askPasswordMsg,
      setAskPasswordMsg,
      isMuted,
      setIsMuted
    }),
    [
      isLoading,
      openInfoMsg,
      openAskBuyin,
      askBuyinTable,
      setOpenAskBuyin,
      buyinAmount,
      isBuyinValueSet,
      userSession,
      openAccessTokenModal,
      manuallyAssignedSeatId,
      openWaitForBuyIn,
      openAddFunds,
      openErrorModal,
      isJoining,
      gameType,
      closeBuyInModal,
      isLocated,
      committedGeoLegacy,
      setCommittedGeoLegacy,
      geoCheckPending,
      setGeoCheckPending,
      isLocationInvalid,
      invalidLocationMsg,
      deviceId,
      duplicateAccessMsg,
      isDuplicateAccess,
      duplicateAccessGameID,
      duplicateAccessGame,
      isTriggerBuyIn,
      openAskPassword,
      askPasswordMsg,
      isMuted,
      setIsMuted
    ]
  );

  return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
};

export default GlobalContext;
